import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  GbgrundbuchgeschaeftService,
  GbgrundstueckService,
  GrundbuchgeschaeftRestDetailEntryMeta,
  GrundstueckId,
  GrundstueckListEntry
} from '../../../core/gb-api';
import { map, switchMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { TimepointService } from '../../../core/services/timepoint.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HistoryMode } from '../../../core/services/HistoryMode';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { LastRechtRootEntryVm, StandardrechtViewmodelService } from '../../../core/services/standardrecht-viewmodel.service';
import { ListLoaderObservable } from '../../../shared/components/list-loader/list-loader-observable';
import { GrundstueckListEntryMeta } from '../../../core/av-api';
import { combineLatest, Subscription } from 'rxjs';
import { FormatKopfzeile, StandardrechtTableComponent } from '../../../shared/widgets/standardrecht-table/standardrecht-table.component';
import { CapExceptionHandler } from '../../../core/extension/exceptions/cap-exception-handler';
import { ComparatorService } from 'app/shared/services/comparator-service';
import { ListLoaderPagination, ListLoaderPaginationBuilder } from '../../../shared/components/list-loader/list-loader-pagination';
import { PaginationLookupResult } from '../../../shared/components/list-loader/pagination-lookup-result';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import { AuszugComponent } from '../../../shared/components/auszug/auszug.component';
import { DetailsGrundbuchgeschaeftComponent } from './details-grundbuchgeschaeft/details-grundbuchgeschaeft.component';
import { VertragsdatenComponent } from './vertragsdaten/vertragsdaten.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { KompaktGrundstueckComponent } from '../../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { MeldungsrelevanteGrundstueckeComponent } from './meldungsrelevante-grundstuecke/meldungsrelevante-grundstuecke.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { FormatGeschaeftsnummerPipe } from '../../../shared/pipes/format/format-geschaeftsnummer.pipe';

@Component({
  selector: 'cap-grundbuchmeldung-detail',
  templateUrl: './grundbuchmeldung-detail.component.html',
  styleUrls: ['./grundbuchmeldung-detail.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    AuszugComponent,
    DetailsGrundbuchgeschaeftComponent,
    VertragsdatenComponent,
    ListLoaderWrapperComponent,
    KompaktGrundstueckComponent,
    MeldungsrelevanteGrundstueckeComponent,
    StandardrechtTableComponent,
    CapSpinnerSmallComponent,
    TranslatePipe,
    FormatGeschaeftsnummerPipe
  ]
})
export class GrundbuchmeldungDetailComponent extends AbstractComponent implements OnInit, OnDestroy {
  loaded = false;
  geschaeft: GrundbuchgeschaeftRestDetailEntryMeta;
  grundstuecke = new Array<GrundstueckListEntry>();
  dienstbarkeitenLoader: ListLoader<LastRechtRootEntryVm>;
  anmerkungenLoader: ListLoader<LastRechtRootEntryVm>;
  formatKopfzeileEnum = FormatKopfzeile;
  grundstueckloader: ListLoaderPagination<GrundstueckListEntryMeta>;

  private readonly ID_PARAMETER_NAME = 'id';
  private readonly FACH_ID_PARAMETER_NAME = 'egbtbid';
  private readonly PAGE_SIZE = 15;
  private readonly CURRENT_PAGE = 1;

  constructor(
    private gbGrundbuchgeschaeftService: GbgrundbuchgeschaeftService,
    private route: ActivatedRoute,
    private timepointService: TimepointService,
    private grundstueckService: GbgrundstueckService,
    private translate: TranslateService,
    private standardrechtViewModelService: StandardrechtViewmodelService,
    protected router: Router,
    private exceptionHandler: CapExceptionHandler,
    private comparatorService: ComparatorService
  ) {
    super();
    keysToTranslate<string>([
      'GrundbuchmeldungZuTagebuch',
      'Servitute',
      '(MitDiesemGeschäftGeschlossen)',
      'AuszugZuGrundbuchmeldungErstellen'
    ]);
  }

  ngOnInit() {
    this.loaded = false;
    this.addSub(
      combineLatest(this.route.params, this.route.queryParams)
        .pipe(
          map((results) => ({ params: results[0], query: results[1] })),
          switchMap((params) => {
            const id = params.params[this.ID_PARAMETER_NAME];
            const egbtbid = params.query[this.FACH_ID_PARAMETER_NAME];
            if (id) {
              this.timepointService.timepointId = id;
              return this.gbGrundbuchgeschaeftService.loadGbGeschaeftById(id);
            } else if (egbtbid) {
              return this.gbGrundbuchgeschaeftService.loadGbGeschaeftByEgbtbid(egbtbid);
            }
            this.router.navigate(['/home/invalidrequest']);
          })
        )
        .subscribe({
          next: (entry) => {
            if (entry.meldungsrelevant !== 'NEIN') {
              this.geschaeft = entry;
              if (!this.timepointService.timepointCtx.timepointId) {
                this.timepointService.timepointId = this.geschaeft.id.id;
              }
              this.grundstueckloader = this.loadMeldungsrelevanteGrundstuecke();
              this.dienstbarkeitenLoader = this.createStandardrechtLoader([], this.subscriptions, [
                'DIENSTBARKEIT',
                'VORMERKUNG',
                'GRUNDLAST',
                'SDR_DIENSTBARKEIT'
              ]);
              this.anmerkungenLoader = this.createStandardrechtLoader([], this.subscriptions, ['ANMERKUNG']);
            } else {
              this.router.navigate(['/home/notfound']);
            }
          },
          error: (err) => {
            if (err.status === 404 || err.status === 400) {
              this.router.navigate(['/home/notfound']);
            } else {
              this.exceptionHandler.handleError(err);
            }
          }
        })
    );
  }

  servitutSortFunction(a, b) {
    return this.comparatorService.lastRechtRootEntryByGsNummerComparator(a, b);
  }

  private loadMeldungsrelevanteGrundstuecke() {
    const sub = (offset, limit, sortColumn) =>
      this.grundstueckService
        .loadGrundstueckSichtGbMeldung(
          this.geschaeft.id.id,
          HistoryMode.HistoryMode.OHNE_HIST_MIT_GESCHAEFTSAENDERUNGEN,
          offset,
          limit,
          'ASC',
          'NUMMER'
        )
        .pipe(
          map(
            (r) =>
              <PaginationLookupResult<GrundstueckListEntryMeta>>{
                result: r.results,
                totalItems: r.totalItems
              }
          )
        );
    const loader = ListLoaderPaginationBuilder.builder<GrundstueckListEntryMeta>()
      .setLookupItemFunc(sub)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .setNoSortModel(true)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createStandardrechtLoader(
    ids: Array<GrundstueckId>,
    subscriptions: Array<Subscription>,
    rechtArt: Array<'DIENSTBARKEIT' | 'VORMERKUNG' | 'ANMERKUNG' | 'GRUNDLAST' | 'PFANDRECHT' | 'SDR_DIENSTBARKEIT' | 'EIGENTUM_ANTEIL'>
  ): ListLoader<LastRechtRootEntryVm> {
    const observable = this.standardrechtViewModelService.loadGbMeldungsSichtByGbGeschaeftId(
      this.geschaeft.id,
      ids,
      subscriptions,
      rechtArt
    );
    const loader = new ListLoaderObservable<LastRechtRootEntryVm>(5, 1, observable);
    this.addLoader(loader);
    return loader;
  }
}
